import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  PRIMITIVE,
  ServiceMessageBroker,
  ServiceMessageBrokerFactory,
} from "shared/provider-webworker";
import { Subject } from "rxjs";

import { TraderGroupLookupService } from "../../lookup/model/impl/TraderGroupLookupService";
import { CubiqWorker } from "../../worker/CubiqWorker";
import { CHANNEL_ACCEPTANCE_SERVICE, IOrderAcceptanceService } from "../IOrderAcceptanceService";
import { OrderAcceptancePromiseService } from "./OrderAcceptancePromiseService";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { SystemConfigPromiseService } from "shared/auth-services";
import { SysFeatures } from "../../model/core/SysFeatures";
import { BFXNotificationSubType } from "../../model/core/notifications/BFXNotificationSubType";
import { BFXNotificationType } from "../../model/core/notifications/BFXNotificationType";
import { NotificationService } from "../../lookup/notification/impl/NotificationService";

/**
 * Execute array of functions (each returning a promise) serially, and return array of results
 * in returned promise
 * @param funcs
 * @returns
 */
const serial = (funcs: (() => Promise<string>)[]): Promise<any[]> =>
  funcs.reduce(
    (promise, func) => promise.then((result) => func().then(Array.prototype.concat.bind(result))),
    Promise.resolve([]),
  );

@Injectable()
export class OrderAcceptancePrimaryService extends OrderAcceptancePromiseService {
  protected broker: ServiceMessageBroker;
  private acceptBatchCount: number = 50;

  constructor(
    private worker: CubiqWorker,
    protected factory: ServiceMessageBrokerFactory,
    private sysConfigService: SystemConfigPromiseService,
    private notifService: NotificationService,
  ) {
    super();
    this.sysConfigService.getSystemConfig(SysFeatures.acceptBatchCount).then((values) => {
      if (values != undefined) {
        if (values == "0") {
          this.acceptBatchCount = undefined;
        } else {
          this.acceptBatchCount = Number(values);
        }
      }
    });
    this.broker = factory.createMessageBroker(CHANNEL_ACCEPTANCE_SERVICE, true, true);

    this.broker.registerMethod(
      "requestAcceptance",
      [PRIMITIVE, PRIMITIVE, PRIMITIVE, PRIMITIVE, PRIMITIVE, PRIMITIVE, PRIMITIVE],
      this.requestAcceptance.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "initModelUpdateService",
      [PRIMITIVE],
      this.initModelUpdateService.bind(this),
      null,
      false,
    );
  }

  /**
   * Divide the acceptance Ids into batches and send each serially to the server. Results will be an array
   * of nulls, or ErrorImpls
   * @param ids
   * @param comment
   * @param acceptanceAction
   * @param acceptanceType
   * @param targetGroupId
   * @param callOrderRateSide
   * @param orderType
   * @param benchmarkOrderType
   * @returns
   */
  public requestAcceptance(
    ids: Array<string>,
    comment: string,
    acceptanceAction: CubiqTS.AcceptanceAction,
    acceptanceType: CubiqTS.AcceptanceType,
    targetGroupId?: string,
    callOrderRateSide?: CubiqTS.RateSideType,
    orderType?: CubiqTS.OrderType,
    benchmarkOrderType?: CubiqTS.BenchmarkOrderType,
  ): Promise<any[]> {
    return new Promise<any>((resolve, reject) => {
      const chunks: any[] = [];
      let chunkSize: number =
        this.acceptBatchCount != undefined ? this.acceptBatchCount : ids.length;
      logger.out.info(
        "OrderAcceptancePrimaryService:" +
          CubiqTS.AcceptanceAction[acceptanceAction] +
          ":" +
          CubiqTS.AcceptanceType[acceptanceType],
      );

      while (ids.length) {
        chunks.push(ids.splice(0, chunkSize));
      }

      let responses: (() => Promise<any>)[] = [];
      while (chunks.length) {
        responses.push(
          this.batchRequestAcceptance.bind(
            this,
            chunks.shift(),
            comment,
            acceptanceAction,
            acceptanceType,
            targetGroupId,
            callOrderRateSide,
            orderType,
            benchmarkOrderType,
          ),
        );
      }

      return serial(responses).then((values) => resolve(values));
    });
  }

  public batchRequestAcceptance(
    ids: Array<string>,
    comment: string,
    acceptanceAction: CubiqTS.AcceptanceAction,
    acceptanceType: CubiqTS.AcceptanceType,
    targetGroupId?: string,
    callOrderRateSide?: CubiqTS.RateSideType,
    orderType?: CubiqTS.OrderType,
    benchmarkOrderType?: CubiqTS.BenchmarkOrderType,
  ): Promise<any> {
    let request = new CubiqTS.OrderAcceptanceRequestImpl();
    request.orderIds = ids != null ? ids.map((a) => (a.toString != null ? a.toString() : a)) : ids;
    request.comment = comment;
    request.acceptanceAction = acceptanceAction;
    request.acceptanceType = acceptanceType;
    request.benchmarkOrderType = benchmarkOrderType;

    if (targetGroupId) {
      request.targetGroupID = CubiqTS.Long.fromString(targetGroupId);
    }
    if (callOrderRateSide in CubiqTS.RateSideType) {
      request.callOrderRateSide = callOrderRateSide;
    }
    if (orderType) {
      request.orderType = orderType;
    }

    let subtype: BFXNotificationSubType = undefined;
    if (request.acceptanceAction === CubiqTS.AcceptanceAction.ACCEPT)
      subtype = BFXNotificationSubType.ACCEPTANCE_FAIL;
    else if (request.acceptanceAction === CubiqTS.AcceptanceAction.REJECT)
      subtype = BFXNotificationSubType.REJECTIONS_FAIL;
    else if (request.acceptanceAction === CubiqTS.AcceptanceAction.ABORT)
      subtype = BFXNotificationSubType.ABORT_FAIL;

    return new Promise<any>((resolve) => {
      this.worker.postServiceRequest(request).then(
        (response) => {
          // Handle any errors returned with a notification then return result
          this.augmentErrorResultIfNeeded(response, ids);
          this.notifService.handleErrors(response, BFXNotificationType.ORDER_ACTION_FAIL, subtype);
          resolve(response);
        },
        (rejectResponse) => {
          // Need to create an Error to display as notification, then return it
          const error = this.createRejectError(rejectResponse);
          this.augmentErrorResultIfNeeded(error, ids);
          this.notifService.handleErrors(error, BFXNotificationType.ORDER_ACTION_FAIL, subtype);
          resolve(error);
        },
      );
    });
  }

  getModelNotificationFailureEventSubject(): Subject<CubiqTS.ConcreteEncodable> {
    return this.modelNotificationFailureEventSubject;
  }
}
